import moment from "moment";
import { Fragment, useEffect, useState } from "react";
import { NumericFormat } from "react-number-format";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import {
  getErpData,
  getPeriodFinancialSheetTree,
  getPeriodFinancialTotal,
} from "../../api/financialSheet";
import _ from "../../i18n";
import {
  getMonthShortLabelObject,
  getTimeByDurationFormat,
  parsePreference,
  toFixed,
} from "../../utils";
import { FinancialIcon } from "../common/Icons";
import PageHeader from "../common/PageHeader/PageHeader";
import FinancialSheetFilter from "./FinancialSheetFilter";
import TreeTable from "./TreeTable/TreeTable";

export default function FinancialSheetTree() {
  const { token, navCommunity, user, collaborator } = useSelector(
    (state) => state.auth
  );
  const { language: lng } = useSelector((state) => state.params);
  const preferences =
    user && navCommunity && user.userOrganizations.length > 0
      ? user.userOrganizations.find(
          (organization) => organization.id === navCommunity.id
        ).authorisation.preferences?.financial
      : {};

  const [data, setData] = useState([]);
  const [totalData, setTotalData] = useState([]);
  const [erpData, setErpData] = useState([]);
  const [nbResults, setNbResults] = useState(0);
  const [filter, setFilter] = useState({
    year: new Date().getFullYear(),
    partner:
      parsePreference(preferences).financialState.allowViewDataBy === 11
        ? user?.id
        : null,
    partnerZone: 1,
    collaborators:
      [10, 110].includes(
        parsePreference(preferences).financialState.allowViewDataBy
      ) && collaborator
        ? [collaborator.id]
        : [],
    clients: [],
    isEntity: null,
    encoursRisk: null,
    role: "",
    deletedStatus: "NOT_DELETED",
    clientCRMGroup: [],
    clientCRMTypes: [],
    keyWord: "",
    sortBy: "REVENUE_DESC",
    pageSize: 10,
    page: 1,
  });

  const monthlySubTabs = getMonthShortLabelObject(lng);
  const currentMonthId = moment().month() + 1;

  const [filterTab, setFilterTab] = useState({
    tabs: [
      {
        id: "MONTHLY",
        label: "monthly",
        subTab: monthlySubTabs,
      },
      {
        id: "TERMLY",
        label: "termly",
        subTab: [
          {
            id: 1,
            label: "1er trimestre (du 01/01 au 30/03)",
            disabled: false,
            start: "01-01",
            end: "03-30",
          },
          {
            id: 2,
            label: "2ème trimestre (du 01/04 au 30/06)",
            disabled: moment().quarter() < 2 && moment().year() == filter.year,
            start: "04-01",
            end: "06-30",
          },
          {
            id: 3,
            label: "3ème trimestre (du 01/07 au 30/09)",
            disabled: moment().quarter() < 3 && moment().year() == filter.year,
            start: "07-01",
            end: "09-30",
          },
          {
            id: 4,
            label: "4ème trimestre (du 01/10 au 30/12)",
            disabled: moment().quarter() < 4 && moment().year() == filter.year,
            start: "10-01",
            end: "12-31",
          },
        ],
      },
      {
        id: "ANNUAL",
        label: "annual",
        start: "01-01",
        end: "12-31",
      },
    ],
    // period: {
    //   id: "MONTHLY",
    //   label: "monthly",
    //   subTab: monthlySubTabs,
    // },
    // subPeriod: monthlySubTabs.find((m) => m.id === currentMonthId),
    period: {
      id: "ANNUAL",
      label: "annual",
      start: "01-01",
      end: "12-31",
    },
    subPeriod: null,
  });

  let totalFinancialSheetQuery = useQuery(
    ["totalFinancialSheet", token, navCommunity, filter, filterTab],
    async () => {
      if (
        token &&
        navCommunity &&
        (([10, 100, 101, 110].includes(
          parsePreference(preferences).financialState.allowViewDataBy
        ) &&
          filter.collaborators.length > 0) ||
          [1, 11].includes(
            parsePreference(preferences).financialState.allowViewDataBy
          ))
      ) {
        try {
          const response = await getPeriodFinancialTotal(
            token,
            navCommunity.id,
            { ...filter, ...filterTab }
          );
          const data = response.data.data;
          setNbResults(data?.count);
          setTotalData(data?.result);
          return data;
        } catch (error) {
          console.error("Financial >> Err: ", error);
          return [];
        }
      }
    },
    {
      enabled: !!token && !!navCommunity && !!filter && !!filterTab,
    }
  );

  let financialSheetQuery = useQuery(
    ["financialSheet", token, navCommunity.id, filter, filterTab],
    async () => {
      if (
        token &&
        navCommunity &&
        (([10, 100, 101, 110].includes(
          parsePreference(preferences).financialState.allowViewDataBy
        ) &&
          filter.collaborators.length > 0) ||
          [1, 11].includes(
            parsePreference(preferences).financialState.allowViewDataBy
          ))
      ) {
        try {
          const response = await getPeriodFinancialSheetTree(
            token,
            navCommunity.id,
            { ...filter, ...filterTab }
          );
          const data = response.data.data;

          setData(data);
          return data;
        } catch (error) {
          console.error("Financial >> Err: ", error);
          return [];
        }
      }
    },
    {
      enabled: !!token && !!navCommunity && !!filter && !!filterTab,
    }
  );

  let erpDataQuery = useQuery(
    ["erpData", token, navCommunity.id, filter],
    async () => {
      if (token && navCommunity) {
        try {
          const response = await getErpData(
            token,
            navCommunity.id,
            filter.collaborators
          );
          const data = response.data.data;
          setErpData(data);
          return data;
        } catch (error) {
          console.error("Financial >> Err: ", error);
          return [];
        }
      }
    },
    {
      enabled: !!token && !!navCommunity,
    }
  );

  const renderPeriodFilter = () => {
    return (
      <div className="mini-tabs-frame">
        {filterTab.tabs.map((item) => (
          <div
            key={item.id}
            className={
              filterTab.period &&
              item.id === filterTab.period.id &&
              "mini-tabs-frame_active"
            }
            onClick={() => {
              setFilterTab({
                ...filterTab,
                period: item,
                subPeriod:
                  "MONTHLY" === item.id
                    ? item.subTab[moment().month()]
                    : "TERMLY" === item.id
                    ? item.subTab[moment().quarter() - 1]
                    : null,
              });
            }}
          >
            {_(item.label)}
          </div>
        ))}
      </div>
    );
  };

  const renderTotalCardsContent = () => {
    const formattedData = [
      {
        name: _("budgetHr"),
        total: totalData?.budgetH?.total,
        children: totalData?.budgetH?.subTotals,
        isTime: true,
      },
      {
        name: _("prestaTs"),
        total: totalData?.PrestaTs?.total,
        diff: totalData?.PrestaTs?.diff,
        children: totalData?.PrestaTs?.subTotals,
        isTime: true,
      },
      {
        name: _("economicBudget"),
        total: totalData?.budgetEco?.total,
        ratio: totalData?.budgetEco?.ratio,
      },
      {
        name: _("turnover"),
        total: totalData?.profit?.revenue,
        ratio: totalData?.profit?.ratio,
      },
      {
        name: _("outstanding"),
        total: totalData?.Encours?.amount,
      },
    ];
    return (
      <Fragment>
        <div className="section-header">{_("totalSummary")}</div>
        <div className="financial-card-row">
          {formattedData &&
            formattedData.map((item, index) => {
              return (
                <div className="card-item" key={`${index}-${item.name}`}>
                  <div className="card-body">
                    <h5 className="card-title">{item?.name}</h5>
                    <div className="card-text">
                      {item?.isTime ? (
                        getTimeByDurationFormat(toFixed(item.total, 2))
                      ) : (
                        <NumericFormat
                          value={item["total"]}
                          displayType={"text"}
                          thousandSeparator={"."}
                          decimalSeparator={","}
                          suffix=" €"
                          decimalScale={2}
                        />
                      )}
                      <span className="text">{_("total")}</span>
                    </div>
                    {item?.children ? (
                      <div className="sub">
                        {item?.children.map((child, index) => {
                          return (
                            <div key={`${index}-${child.name}`}>
                              <p className="card-text small">{child.zone}</p>
                              <span>
                                {getTimeByDurationFormat(
                                  toFixed(child.value, 2)
                                )}
                              </span>
                              <div className="diff-badge">
                                {`${parseFloat(
                                  (child.value / item.total) * 100
                                ).toFixed(1)} %`}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                    {item?.ratio ? (
                      <div className="sub">
                        <div>
                          <p className="card-text small">
                            {item.name == _("economicBudget")
                              ? _("budgetRate")
                              : _("effectifRate")}
                          </p>
                          <NumericFormat
                            value={item["ratio"]}
                            displayType={"text"}
                            thousandSeparator={"."}
                            decimalSeparator={","}
                            suffix=" €/h"
                            decimalScale={2}
                          />
                        </div>
                      </div>
                    ) : null}
                    {item?.diff ? (
                      <span
                        className="float-badge"
                        style={{
                          "--color": item["diff"] <= 0 ? "#02af8e" : "#de4848",
                          "--bg-color":
                            item["diff"] <= 0 ? "#02af8e25" : "#de484825",
                        }}
                      >
                        <NumericFormat
                          value={item["diff"]}
                          displayType={"text"}
                          thousandSeparator={"."}
                          decimalSeparator={","}
                          suffix=" %"
                        />
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
        </div>
      </Fragment>
    );
  };

  const renderTabContent = () => {
    return (
      <TreeTable
        data={data}
        erpData={erpData}
        nbResults={nbResults}
        filter={filter}
        setFilter={setFilter}
      />
    );
  };

  return (
    <div>
      <PageHeader
        header={_("financial-sheet")}
        subHeader={_("subTitleFinancialSheet")}
        icon={<FinancialIcon size="36" fill="#29394D" />}
      />
      {/* {renderDeltaBubble()} */}

      <div className="container" style={{ padding: "0 70px" }}>
        <FinancialSheetFilter
          token={token}
          navCommunity={navCommunity}
          nbResults={nbResults}
          filter={filter}
          customAddOn={renderPeriodFilter()}
          setFilter={setFilter}
          collaborator={collaborator}
          preferences={preferences}
        />
        {filterTab.period && "ANNUAL" !== filterTab.period.id && (
          <div className="additional-line">
            {filterTab.period.subTab.map((tab) => {
              return (
                <div
                  className={`tag ${
                    filterTab.subPeriod.id === tab.id ? "active" : ""
                  } ${
                    ((tab.disabled &&
                      filterTab.period &&
                      filterTab.period.id === "TERMLY") ||
                      (filterTab.period &&
                        filterTab.period.id === "MONTHLY" &&
                        moment().year() == filter.year &&
                        tab.id > moment().month() + 1)) &&
                    "disabled"
                  }`}
                  onClick={() =>
                    !tab.disabled
                      ? setFilterTab({
                          ...filterTab,
                          subPeriod: tab,
                        })
                      : null
                  }
                >
                  {tab.label}
                </div>
              );
            })}
          </div>
        )}
        {totalFinancialSheetQuery.isFetched && renderTotalCardsContent()}
        {financialSheetQuery.isFetching ? (
          <div className="ripple_loading">
            <img src={"/img/Ripple-1s-150px.svg"} alt="ripple_loading" />
          </div>
        ) : financialSheetQuery.isFetched ? (
          data && renderTabContent()
        ) : (
          <div className="no_data">
            <div className="title">{_("noDataFound")}</div>
          </div>
        )}
      </div>
    </div>
  );
}
